'use client';

import { useState, useTransition, useEffect } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SettingsSectionWrapper } from './settings-section-wrapper';
import { updateYearlyLimit } from '@/actions/yearly-limits/update-yearly-limit';
import { TLimit } from '@/types';

const DEFAULT_LIMIT = 60000;
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2020; // Adjust based on your needs

interface YearlyLimitsFormProps {
  limits: TLimit[];
}

type YearlyLimitFormValues = {
  [key: string]: number;
};

export function YearlyLimitsForm({ limits }: YearlyLimitsFormProps) {
  const t = useTranslations('UserSettings');
  const common = useTranslations('Common');

  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');

  const [isPending, startTransition] = useTransition();

  // Get years to display: current year and previous years from limits, plus some historical years
  const yearsToShow = Array.from(
    new Set([
      ...limits.map((l) => l.year),
      CURRENT_YEAR,
      CURRENT_YEAR - 1,
      CURRENT_YEAR - 2,
      CURRENT_YEAR - 3,
    ])
  )
    .filter((year) => year >= MIN_YEAR && year <= CURRENT_YEAR)
    .sort((a, b) => b - a); // Sort descending (newest first)

  // Create default values from existing limits, with default 60000 for missing years
  const defaultValues: YearlyLimitFormValues = {};
  yearsToShow.forEach((year) => {
    const existingLimit = limits.find((l) => l.year === year);
    defaultValues[year.toString()] = existingLimit?.limit || DEFAULT_LIMIT;
  });

  // Create a dynamic schema for validation
  const schemaFields: Record<string, z.ZodNumber> = {};
  yearsToShow.forEach((year) => {
    schemaFields[year.toString()] = z.number().positive({
      message: 'Limit must be greater than 0',
    });
  });

  const form = useForm<YearlyLimitFormValues>({
    resolver: zodResolver(z.object(schemaFields)),
    defaultValues,
  });

  useEffect(() => {
    // Reset form when limits change
    yearsToShow.forEach((year) => {
      const existingLimit = limits.find((l) => l.year === year);
      form.setValue(year.toString(), existingLimit?.limit || DEFAULT_LIMIT);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limits]);

  const onSubmit = async (values: YearlyLimitFormValues) => {
    setError('');
    setSuccess('');

    // Update all changed limits
    const updatePromises = Object.entries(values).map(([yearStr, limit]) => {
      const year = parseInt(yearStr, 10);
      const existingLimit = limits.find((l) => l.year === year);
      const existingValue = existingLimit?.limit || DEFAULT_LIMIT;

      // Only update if the value has changed
      if (limit !== existingValue) {
        return updateYearlyLimit({ year, limit });
      }
      return Promise.resolve({ success: undefined });
    });

    startTransition(async () => {
      try {
        const results = await Promise.all(updatePromises);
        const errors = results.filter(
          (r): r is { error: string } => 'error' in r && !!r.error
        );
        const successes = results.filter(
          (r): r is { success: string } => 'success' in r && !!r.success
        );

        if (errors.length > 0) {
          setError(
            errors[0].error ||
              'Some limits could not be updated. Please try again.'
          );
        } else if (successes.length > 0) {
          setSuccess('Yearly limits updated successfully!');
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setSuccess('No changes detected.');
          setTimeout(() => setSuccess(''), 3000);
        }
      } catch (err) {
        setError('Something went wrong!');
        console.error(err);
      }
    });
  };

  return (
    <SettingsSectionWrapper
      title={t('yearlyLimitsTitle')}
      description={t('yearlyLimitsDescription')}
      error={error}
      success={success}
      footer={
        <div className="flex gap-3">
          <Button
            type="submit"
            form="yearly-limits-form"
            className="flex-1"
            disabled={isPending}
          >
            {isPending ? common('saving') || 'Saving...' : common('save')}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id="yearly-limits-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {yearsToShow.map((year) => (
              <FormField
                key={year}
                control={form.control}
                name={year.toString()}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {year === CURRENT_YEAR ? (
                        <span>
                          {year}{' '}
                          <span className="text-muted-foreground">
                            (Current)
                          </span>
                        </span>
                      ) : (
                        year.toString()
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        disabled={isPending}
                        placeholder={DEFAULT_LIMIT.toString()}
                        value={
                          field.value === undefined || field.value === null
                            ? ''
                            : field.value
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            field.onChange(DEFAULT_LIMIT);
                            return;
                          }
                          const numValue = parseFloat(value);
                          field.onChange(
                            isNaN(numValue) ? DEFAULT_LIMIT : numValue
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </form>
      </Form>
    </SettingsSectionWrapper>
  );
}
